import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useResources } from '@/contexts/ResourceContext';
import type { ResourceCategory } from '@/contexts/ResourceContext';

const categoryColors: Record<ResourceCategory, string> = {
  food: '#22c55e',
  shelter: '#8b5cf6',
  medical: '#ef4444',
  skills: '#3b82f6',
  tools: '#f59e0b',
  transport: '#06b6d4',
  legal: '#ec4899',
  childcare: '#f97316',
  elderlycare: '#6366f1',
  'mental-health': '#a855f7',
  community: '#14b8a6',
  other: '#6b7280',
};

export function NetworkStats() {
  const { resources } = useResources();

  // Calculate stats
  const totalResources = resources.length;
  const verifiedResources = resources.filter((r) => r.verified).length;
  const verificationRate = totalResources > 0 ? Math.round((verifiedResources / totalResources) * 100) : 0;
  // Count unique locations/addresses as "communities"
  const communities = new Set(resources.map(r => r.address)).size || 1;

  // Category breakdown
  const categoryCounts = resources.reduce((acc, r) => {
    acc[r.category] = (acc[r.category] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const sortedCategories = Object.entries(categoryCounts)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5);

  const maxCount = sortedCategories.length > 0 ? sortedCategories[0][1] : 1;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
      {/* Main stats */}
      <div className="md:col-span-2 grid grid-cols-2 gap-3 sm:gap-4">
        {/* Total Resources */}
        <Card className="border-0 shadow-md overflow-hidden group hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Total Resources</p>
                <p className="text-4xl font-bold gradient-text">{totalResources}</p>
              </div>
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center group-hover:scale-110 transition-transform">
                <svg className="w-7 h-7 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Verified */}
        <Card className="border-0 shadow-md overflow-hidden group hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Verified</p>
                <p className="text-4xl font-bold">{verifiedResources}</p>
              </div>
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center group-hover:scale-110 transition-transform">
                <svg className="w-7 h-7 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Verification rate */}
        <Card className="border-0 shadow-md overflow-hidden group hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Verification Rate</p>
                <p className="text-4xl font-bold">{verificationRate}%</p>
              </div>
              <div className="relative w-14 h-14">
                <svg className="w-14 h-14 -rotate-90" viewBox="0 0 36 36">
                  <circle
                    cx="18"
                    cy="18"
                    r="16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    className="text-muted"
                  />
                  <circle
                    cx="18"
                    cy="18"
                    r="16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeDasharray={`${verificationRate} 100`}
                    strokeLinecap="round"
                    className="text-primary"
                  />
                </svg>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Communities */}
        <Card className="border-0 shadow-md overflow-hidden group hover:shadow-lg transition-shadow">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Communities</p>
                <p className="text-4xl font-bold">{communities}</p>
              </div>
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-accent/20 to-accent/5 flex items-center justify-center group-hover:scale-110 transition-transform">
                <svg className="w-7 h-7 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Category breakdown */}
      <Card className="border-0 shadow-md overflow-hidden">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium">Top Categories</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {sortedCategories.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              No resources yet
            </p>
          ) : (
            sortedCategories.map(([category, count]) => {
              const color = categoryColors[category as ResourceCategory] || '#6b7280';
              const percentage = Math.round((count / maxCount) * 100);
              const displayName: Record<string, string> = {
                medical: 'Health',
              };
              const label = displayName[category] || category.replace('-', ' ');

              return (
                <div key={category} className="space-y-1.5">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium capitalize">{label}</span>
                    <span className="text-muted-foreground">{count}</span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500 ease-out"
                      style={{
                        width: `${percentage}%`,
                        backgroundColor: color,
                      }}
                    />
                  </div>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>
    </div>
  );
}
